/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import React, { useState, useRef } from 'react';
import { Modal, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import {
  PlusIcon,
  UserGroupIcon,
  ClipboardCheckIcon,
  DocumentAddIcon,
  UserAddIcon,
  PencilAltIcon,
} from '@heroicons/react/solid';
import { TweetyWidget } from '@vlinder-web/tweety-widget-react';
import { KanbanAvatar } from './components';
import { ItemSpecMercuryWidget } from '@vlinder-web/item-spec-mercury-widget-react';
import {
  ITimelineChildProps,
  TimelineMercury,
} from '@vlinder-web/timeline-mercury-widget-react';
import { useTheme } from '../../../../providers/theme';
import './styles.css';

export * from './components';

export interface ISupplyChainStepModalProps {
  show?: boolean;
  onHide?(): any;
  backdrop?: 'static' | boolean;
  centered?: boolean;
  fullScreen?:
    | true
    | 'sm-down'
    | 'md-down'
    | 'lg-down'
    | 'xl-down'
    | 'xxl-down';
  isVerticallyCentered?: boolean;
  widgetProps: any;
  size?: 'xm' | 'lg' | 'xl';
  submitButtonTitle?: string;
  scrollable?: boolean;
  onSubmit?(payload: any): void;
  onEditMembersCB?(payload?: any): any;
  onEditComplianceCB?(payload?: any): any;
  onEditSupplyChainStepFormCB?(payload?: any): any;
  onEditRecipientsCB?(payload?: any): any;
  onUpdateTraceStepCB?(payload?: any): any;
  [key: string]: any;
  provananceData?: any;
  timelineData?: any;
}

export const SupplyChainStepModal = ({
  show,
  onHide,
  backdrop,
  fullScreen,
  centered,
  isVerticallyCentered,
  widgetProps,
  size,
  submitButtonTitle,
  onSubmit,
  scrollable,
  onEditMembersCB,
  onEditComplianceCB,
  onEditRecipientsCB,
  onEditSupplyChainStepFormCB,
  onUpdateTraceStepCB,
  provananceData,
  timelineData,
  ...rest
}: ISupplyChainStepModalProps) => {
  const [showModal, setShowModal] = useState(show);
  const theme = useTheme();
  const {
    id: cardId,
    index,
    subtitle: description,
    listId,
    members = [],
    complianceProof = [],
    recipients = [],
    supplyChainStepOutput,
    traceStepTemplate = [],
  } = widgetProps;
  const [title, setTitle] = useState(widgetProps.label ?? '');
  const [comment, setComment] = useState('');
  const [isTitleEditable, setIsTitleEditable] = useState(false);

  const modalRef = useRef<any>();

  const toggleIsTitleEditable = () => {
    setIsTitleEditable(!isTitleEditable);
  };

  const onHideCB = () => {
    setShowModal(false);
    onHide && onHide();
  };
  const props: any = {};
  if (isVerticallyCentered) {
    props['aria-labelledby'] = 'contained-modal-title-vcenter';
  }
  if (size) {
    props.size = size;
  }

  const onChange = () => {
    const payload = { listId, cardId, title };

    if (title !== props.title) {
      widgetProps.onChange && widgetProps.onChange(payload);
    }

    toggleIsTitleEditable();
  };

  const onEditMembers = () => {
    onEditMembersCB && onEditMembersCB(widgetProps);
  };

  const onEditCompliance = () => {
    onEditComplianceCB && onEditComplianceCB(widgetProps);
  };

  const onEditRecipients = () => {
    onEditRecipientsCB && onEditRecipientsCB(widgetProps);
  };

  const onUpdateTraceStep = () => {
    onUpdateTraceStepCB &&
      onUpdateTraceStepCB({
        complianceProof: complianceProof,
        suppliers: members,
        recipients: recipients,
      });
    setShowModal(false);
  };

  const _renderButtons = () => {
    if (submitButtonTitle) {
      return (
        <>
          <Button
            variant="link"
            className="text-gray ms-auto"
            onClick={onHideCB}
          >
            Close
          </Button>
        </>
      );
    }
  };

  const onEditSupplyStepForm = () => {
    onEditSupplyChainStepFormCB && onEditSupplyChainStepFormCB(widgetProps);
  };
  console.log('provananceData', provananceData);
  return (
    <Modal
      as={Modal.Dialog}
      centered={centered}
      show={showModal}
      onHide={onHideCB}
      backdrop={backdrop}
      fullscreen={fullScreen}
      scrollable={scrollable}
      {...props}
      animation={false}
    >
      <Modal.Header className="border-0">
        <Button variant="close" aria-label="Close" onClick={onHideCB} />
      </Modal.Header>
      <ItemSpecMercuryWidget {...provananceData} />
      <TimelineMercury
        items={timelineData}
        onItemClick={() => {}}
        lineColor={theme?.colors?.primaryTimelineTag}
      />
      <Modal.Footer>{_renderButtons()}</Modal.Footer>
    </Modal>
  );
};

SupplyChainStepModal.defaultProps = {
  show: false,
  backdrop: true,
  centered: true,
  isVerticallyCentered: false,
  submitButtonTitle: 'Submit',
  scrollable: true,
  fullScreen: undefined,
};
